import React, { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Settings } from "lucide-react";
import * as api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../ui/dialog";
import { EmptyState } from "../shared/EmptyState";
import { PageLoader } from "../shared/LoadingSpinner";
import { toast } from "sonner";

export function AdminWorkstationTemplates() {
    const [templates, setTemplates] = useState([]);
    const [software, setSoftware] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        software_ids: [],
        ad_groups: "",
        network_shares: "",
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [templatesData, softwareData] = await Promise.all([
                api.getWorkstationTemplates(),
                api.getSoftwareCatalog(),
            ]);
            setTemplates(templatesData);
            setSoftware(softwareData);
        } catch (error) {
            toast.error("Erreur lors du chargement");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditing(item);
            setFormData({
                name: item.name,
                software_ids: item.software_ids || [],
                ad_groups: (item.ad_groups || []).join(", "),
                network_shares: (item.network_shares || []).join(", "),
            });
        } else {
            setEditing(null);
            setFormData({ name: "", software_ids: [], ad_groups: "", network_shares: "" });
        }
        setDialogOpen(true);
    };

    const toggleSoftware = (swId) => {
        setFormData((prev) => ({
            ...prev,
            software_ids: prev.software_ids.includes(swId)
                ? prev.software_ids.filter((id) => id !== swId)
                : [...prev.software_ids, swId],
        }));
    };

    const handleSave = async () => {
        try {
            const data = {
                name: formData.name,
                software_ids: formData.software_ids,
                ad_groups: formData.ad_groups.split(",").map((s) => s.trim()).filter(Boolean),
                network_shares: formData.network_shares.split(",").map((s) => s.trim()).filter(Boolean),
            };

            if (editing) {
                await api.updateWorkstationTemplate(editing.id, data);
                toast.success("Modèle mis à jour");
            } else {
                await api.createWorkstationTemplate(data);
                toast.success("Modèle créé");
            }
            setDialogOpen(false);
            loadData();
        } catch (error) {
            toast.error("Erreur lors de la sauvegarde");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce modèle ?")) return;
        try {
            await api.deleteWorkstationTemplate(id);
            toast.success("Modèle supprimé");
            loadData();
        } catch (error) {
            toast.error("Erreur lors de la suppression");
        }
    };

    if (loading) return <PageLoader />;

    return (
        <div className="page-container" data-testid="admin-templates">
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">Modèles de postes</h1>
                    <p className="page-subtitle">{templates.length} modèles</p>
                </div>
                <Button onClick={() => handleOpenDialog()} data-testid="add-template-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter
                </Button>
            </div>

            {templates.length === 0 ? (
                <div className="card">
                    <EmptyState
                        icon={Settings}
                        title="Aucun modèle"
                        description="Créez des modèles de postes de travail"
                    />
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {templates.map((tpl) => (
                        <div key={tpl.id} className="card" data-testid={`template-card-${tpl.id}`}>
                            <div className="card-body">
                                <div className="flex items-start justify-between mb-4">
                                    <h3 className="font-semibold text-gray-900">{tpl.name}</h3>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleOpenDialog(tpl)}
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDelete(tpl.id)}
                                        >
                                            <Trash2 className="w-4 h-4 text-red-500" />
                                        </Button>
                                    </div>
                                </div>
                                <div className="space-y-3 text-sm">
                                    <div>
                                        <span className="text-gray-500">Logiciels:</span>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {tpl.software_ids?.map((swId) => {
                                                const sw = software.find((s) => s.id === swId);
                                                return sw ? (
                                                    <span
                                                        key={swId}
                                                        className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs"
                                                    >
                                                        {sw.name}
                                                    </span>
                                                ) : null;
                                            })}
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Groupes AD:</span>
                                        <p className="text-gray-900">
                                            {tpl.ad_groups?.join(", ") || "—"}
                                        </p>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Partages réseau:</span>
                                        <p className="text-gray-900">
                                            {tpl.network_shares?.join(", ") || "—"}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {editing ? "Modifier le modèle" : "Ajouter un modèle"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="form-label">Nom *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Ex: Poste standard"
                                data-testid="template-name"
                            />
                        </div>
                        <div>
                            <Label className="form-label">Logiciels inclus</Label>
                            <div className="grid grid-cols-2 gap-2 border rounded-md p-3 max-h-48 overflow-y-auto">
                                {software.map((sw) => (
                                    <label
                                        key={sw.id}
                                        className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer"
                                    >
                                        <Checkbox
                                            checked={formData.software_ids.includes(sw.id)}
                                            onCheckedChange={() => toggleSoftware(sw.id)}
                                        />
                                        <span className="text-sm">{sw.name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label className="form-label">Groupes Active Directory</Label>
                            <Input
                                value={formData.ad_groups}
                                onChange={(e) => setFormData({ ...formData, ad_groups: e.target.value })}
                                placeholder="Séparez par des virgules"
                                data-testid="template-groups"
                            />
                        </div>
                        <div>
                            <Label className="form-label">Partages réseau</Label>
                            <Input
                                value={formData.network_shares}
                                onChange={(e) => setFormData({ ...formData, network_shares: e.target.value })}
                                placeholder="Séparez par des virgules"
                                data-testid="template-shares"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleSave} disabled={!formData.name} data-testid="save-template-btn">
                            {editing ? "Enregistrer" : "Créer"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
